/**
 * Création de compte (onglet « Créer un compte » de l'écran de connexion).
 *
 * ⚠️ Créer un compte ne donne AUCUN droit : rien n'est écrit côté client (ni
 * rôle, ni profil, ni équipe). Les accès sont accordés ensuite par un
 * administrateur ; tant que ce n'est pas fait, la console refuse le compte avec
 * son message habituel. Ces fonctions sont pures (sans réseau) pour être testées.
 */

export const LONGUEUR_MIN_MOT_DE_PASSE = 8

export interface SaisieInscription {
  nom: string
  email: string
  motDePasse: string
  confirmation: string
}

export type ChampInscription = 'nom' | 'email' | 'motDePasse' | 'confirmation'
export type ErreursInscription = Partial<Record<ChampInscription, string>>

export const MESSAGE_COMPTE_EXISTANT =
  'Un compte existe déjà avec cette adresse : connectez-vous, ou utilisez « Mot de passe oublié ».'

const FORMAT_EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/

/** Validation côté client, dans l'ordre d'affichage des champs. Vide = tout est bon. */
export function validerInscription(s: SaisieInscription): ErreursInscription {
  const e: ErreursInscription = {}
  if (!s.nom.trim()) e.nom = 'Indiquez votre nom complet.'
  if (!FORMAT_EMAIL.test(s.email.trim())) e.email = 'Adresse e-mail invalide.'
  if (s.motDePasse.length < LONGUEUR_MIN_MOT_DE_PASSE) {
    e.motDePasse = `Le mot de passe doit contenir au moins ${LONGUEUR_MIN_MOT_DE_PASSE} caractères.`
  }
  if (s.confirmation !== s.motDePasse) e.confirmation = 'Les deux mots de passe ne correspondent pas.'
  return e
}

/** Première erreur dans l'ordre des champs (celle qu'on montre et qu'on focalise). */
export function premiereErreur(e: ErreursInscription): { champ: ChampInscription; message: string } | null {
  for (const champ of ['nom', 'email', 'motDePasse', 'confirmation'] as const) {
    const message = e[champ]
    if (message) return { champ, message }
  }
  return null
}

/** Message affiché une fois le compte créé (confirmation par e-mail activée). */
export function messageCompteCree(email: string): string {
  return `Compte créé. Ouvrez le lien de confirmation envoyé à ${email}, puis connectez-vous.`
}

/** Traduit une erreur Supabase Auth (`message`, `code`, `status`) en français. */
export function traduireErreurInscription(message: string, code?: string, status?: number): string {
  const texte = `${code ?? ''} ${message}`.toLowerCase()
  if (/already registered|already been registered|user_already_exists|email_exists/.test(texte)) {
    return MESSAGE_COMPTE_EXISTANT
  }
  if (status === 429 || /rate limit|rate_limit|too many|for security purposes/.test(texte)) {
    return 'Trop de tentatives, réessayez dans quelques minutes.'
  }
  if (/weak_password|weak password|password should|at least \d+ characters|pwned|easy to guess/.test(texte)) {
    return `Mot de passe trop faible : ${LONGUEUR_MIN_MOT_DE_PASSE} caractères au moins, mêlant lettres et chiffres.`
  }
  if (/email_address_invalid|invalid email|email.*invalid|unable to validate email/.test(texte)) {
    return 'Adresse e-mail invalide.'
  }
  if (/signup_disabled|signups? (are |is )?(not allowed|disabled)/.test(texte)) {
    return 'La création de compte est fermée pour le moment. Contactez-nous.'
  }
  if (/failed to fetch|network|fetch failed/.test(texte)) {
    return 'Connexion au serveur impossible. Vérifiez votre connexion et réessayez.'
  }
  return 'Création du compte impossible. Réessayez dans un instant.'
}

/**
 * Avec la confirmation par e-mail, Supabase ne renvoie PAS d'erreur pour une
 * adresse déjà inscrite : il rend un utilisateur factice sans aucune identité.
 */
export function adresseDejaInscrite(user: { identities?: unknown[] | null } | null | undefined): boolean {
  return !!user && Array.isArray(user.identities) && user.identities.length === 0
}
